// @spec VUL-STR-004, VUL-STR-005, VUL-STR-006, VUL-STR-018, VUL-STR-022, VUL-STR-023, VUL-STR-025, VUL-STR-026, VUL-STR-032, VUL-STR-037, VUL-STR-038, VUL-STR-061
'use client';

import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArchiveVulnerabilityDialog,
  BulkLinkAssetsDialog,
  ConfirmVulnerabilityDialog,
  CreateRemediationTaskDialog,
  CreateRiskFromVulnerabilityDialog,
  EditVulnerabilityDialog,
  LinkRiskDialog,
  RequestAcceptanceDialog,
  RestoreVulnerabilityDialog,
  VerifyRemediationDialog,
  VulnerabilityStatusChangeDialog,
  useAvailableRisks,
  useBulkLinkAssets,
  useLinkRisk,
  useUnlinkAsset,
  useUnlinkRisk,
  useUpdateAssetLink,
  useVulnerability,
  VulnerabilityAffectedAssets,
  VulnerabilityChangeHistorySection,
  VulnerabilityDetailHeader,
  VulnerabilityExceptionPanel,
  AddCompensatingControlDialog,
  CompensatingControlsPanel,
  VulnerabilityLinkedEntities,
  VulnerabilityRemediationStatus,
} from '@/features/vulnerabilities';
import { NIS2MeasureLinkSection } from '@/features/nis2/components/NIS2MeasureLinkSection';
import { useThreats } from '@/features/threats/api/use-threats';
import type { VulnerabilityStatus } from '@/features/vulnerabilities';
import { getVulnerabilityFrameworkLabels, getVulnerabilityWorkflowType } from '@/features/vulnerabilities/workflow-type';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';

export const Route = createFileRoute('/_authenticated/vulnerabilities/$vulnerabilityId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.vulnerabilityDetails'),
  }),
  component: VulnerabilityDetailPage,
});

function getCustomCount(customFields: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = customFields[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function getCustomStringArray(customFields: Record<string, unknown>, keys: string[]): string[] {
  for (const key of keys) {
    const value = customFields[key];
    if (Array.isArray(value)) {
      const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
      if (items.length > 0) {
        return items;
      }
    }
  }
  return [];
}

function getDisplayVulnerabilityId(vulnerability: {
  id: string;
  customFields: Record<string, unknown>;
}): string {
  for (const key of ['id', 'vulnerabilityId', 'internalId', 'customId']) {
    const value = vulnerability.customFields[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return vulnerability.id;
}

function getVulnerabilityIdType(vulnerability: { customFields: Record<string, unknown> }): 'system' | 'custom' {
  for (const key of ['id', 'vulnerabilityId', 'internalId', 'customId']) {
    const value = vulnerability.customFields[key];
    if (typeof value === 'string' && value.trim()) {
      return 'custom';
    }
  }

  return 'system';
}

/**
 * Vulnerability detail page showing comprehensive vulnerability information.
 *
 * Displays:
 * - Header with ID/title and status badges
 * - Table-aligned overview (ID, type, tags, frameworks, linked items, last update)
 * - General context & links (description, risks, threats, assets)
 * - Affected assets
 * - Remediation status and linked tasks
 * - Linked risks and incidents
 * - Exception/acceptance panel (if applicable)
 */
function VulnerabilityDetailPage() {
  const { t } = useTranslation('vulnerabilities');
  const navigate = useNavigate();
  const { vulnerabilityId } = useParams({
    from: '/_authenticated/vulnerabilities/$vulnerabilityId',
  });
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const activeOrg = organizations.find((org) => org.isActive);

  const {
    vulnerability,
    isLoading: vulnerabilityLoading,
    isError,
    error,
    refetch,
  } = useVulnerability({
    vulnerabilityId,
    enabled: Boolean(activeOrg),
  });
  const linkedThreatsQuery = useThreats({
    vulnerabilityId,
    enabled: Boolean(activeOrg),
  });

  // Archive/Restore/Confirm dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = React.useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  // VUL-STR-022: Status change dialog state
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = React.useState(false);
  const [statusChangeTarget, setStatusChangeTarget] =
    React.useState<VulnerabilityStatus>('confirmed');
  // VUL-STR-026: Create remediation task dialog state
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = React.useState(false);
  const [addControlDialogOpen, setAddControlDialogOpen] = React.useState(false);
  // VUL-STR-027: Verify remediation dialog state
  const [verifyDialogOpen, setVerifyDialogOpen] = React.useState(false);
  // VUL-STR-032: Request acceptance dialog state
  const [acceptanceDialogOpen, setAcceptanceDialogOpen] = React.useState(false);
  // VUL-STR-038: Create risk from vulnerability dialog state
  const [createRiskDialogOpen, setCreateRiskDialogOpen] = React.useState(false);

  // VUL-STR-018/020: Asset linking
  const [linkAssetsDialogOpen, setLinkAssetsDialogOpen] = React.useState(false);
  const bulkLinkAssets = useBulkLinkAssets(vulnerabilityId);
  const updateAssetLink = useUpdateAssetLink(vulnerabilityId);
  const unlinkAsset = useUnlinkAsset(vulnerabilityId);

  // VUL-STR-037: Risk linking state and mutations
  const [linkRiskDialogOpen, setLinkRiskDialogOpen] = React.useState(false);
  const [riskSearchTerm, setRiskSearchTerm] = React.useState('');
  const { data: availableRisksData, isLoading: isLoadingAvailableRisks } = useAvailableRisks(
    vulnerabilityId,
    riskSearchTerm,
    1,
    50,
    linkRiskDialogOpen
  );
  const linkRisk = useLinkRisk(vulnerabilityId);
  const unlinkRisk = useUnlinkRisk(vulnerabilityId);

  const handleBack = () => {
    void navigate({ to: '/vulnerabilities' });
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleArchiveSuccess = () => {
    void refetch();
  };

  const handleRestoreSuccess = () => {
    void refetch();
  };

  // VUL-STR-023: Confirm vulnerability success handler
  const handleConfirmSuccess = () => {
    void refetch();
  };

  // VUL-STR-026: Create remediation task success handler
  const handleCreateTaskSuccess = () => {
    void refetch();
  };

  // VUL-STR-022: Status change handler
  const handleStatusChange = (targetStatus: VulnerabilityStatus) => {
    setStatusChangeTarget(targetStatus);
    setStatusChangeDialogOpen(true);
  };

  const handleStatusChangeSuccess = () => {
    void refetch();
  };

  // VUL-STR-027: Verify remediation success handler
  const handleVerifySuccess = () => {
    void refetch();
  };

  // VUL-STR-032: Request acceptance success handler
  const handleAcceptanceSuccess = () => {
    void refetch();
  };

  // VUL-STR-038: Create risk from vulnerability success handler
  const handleCreateRiskSuccess = () => {
    void refetch();
  };

  // VUL-STR-018: Asset linking handlers
  const handleLinkAssets = async (assetIds: string[], status: string) => {
    await bulkLinkAssets.mutateAsync({ assetIds, status });
  };

  const handleUpdateAssetStatus = async (assetId: string, status: string) => {
    await updateAssetLink.mutateAsync({ assetId, request: { status } });
  };

  const handleUnlinkAsset = async (assetId: string, reason: string) => {
    await unlinkAsset.mutateAsync({ assetId, request: { reason } });
  };

  // VUL-STR-037: Risk linking handlers
  const handleLinkRisk = async (request: {
    riskId: string;
    linkReason?: string;
    notes?: string;
  }) => {
    await linkRisk.mutateAsync(request);
  };

  const handleUnlinkRisk = async (linkId: string, reason?: string) => {
    if (reason) {
      await unlinkRisk.mutateAsync({ linkId, request: { reason } });
    } else {
      await unlinkRisk.mutateAsync({ linkId });
    }
  };

  // Loading state
  if (orgsLoading || vulnerabilityLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-8 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-8 w-96" />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // No active organization
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detailPage.noOrgSelected')}</AlertTitle>
            <AlertDescription>
              {t('detailPage.noOrgSelectedDesc')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/settings/organizations' })}
          >
            {t('detailPage.goToOrganizations')}
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    const apiError = error instanceof ApiClientError ? error : null;
    const is404 = apiError?.status === 404;
    const is403 = apiError?.status === 403;
    const is422 = apiError?.status === 422;

    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {is404
                ? t('detailPage.notFound')
                : is403
                  ? t('detailPage.accessDenied')
                  : is422
                    ? t('detailPage.invalidId')
                    : t('detailPage.error')}
            </AlertTitle>
            <AlertDescription>
              {is404
                ? t('detailPage.notFoundDesc')
                : is403
                  ? t('detailPage.accessDeniedDesc')
                  : is422
                    ? t('detailPage.invalidIdDesc', { id: vulnerabilityId })
                    : apiError?.message ?? t('detailPage.errorDesc')}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/vulnerabilities">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('detailPage.backToVulnerabilities')}
              </Link>
            </Button>
            {!is404 && !is403 && (
              <Button variant="outline" onClick={() => void refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('detailPage.retry')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No vulnerability data
  if (!vulnerability) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detailPage.notFound')}</AlertTitle>
            <AlertDescription>
              {t('detailPage.notFoundSimple')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" asChild>
            <Link to="/vulnerabilities">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('detailPage.backToVulnerabilities')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const workflowType = getVulnerabilityWorkflowType(vulnerability);
  const isPotentialWorkflow = workflowType === 'potential';
  const displayId = getDisplayVulnerabilityId(vulnerability);
  const idType = getVulnerabilityIdType(vulnerability);
  const frameworkLabels = getVulnerabilityFrameworkLabels(vulnerability);
  const liveLinkedThreats = linkedThreatsQuery.data?.items ?? [];
  const linkedThreatsCount = linkedThreatsQuery.data
    ? liveLinkedThreats.length
    : getCustomCount(vulnerability.customFields, ['linkedThreatsCount', 'threatCount', 'threatsCount']) ??
      0;
  const linkedThreatLabels = linkedThreatsQuery.data
    ? liveLinkedThreats.map((threat) => threat.title)
    : getCustomStringArray(vulnerability.customFields, ['linkedThreats', 'threats', 'threatLabels']);
  const linkedSuppliersCount =
    getCustomCount(vulnerability.customFields, [
      'linkedSuppliersCount',
      'supplierCount',
      'suppliersCount',
    ]) ?? 0;
  const linkedItemsSummary = [
    { label: t('detailPage.risks'), count: vulnerability.linkedRisks.length },
    { label: t('detailPage.threats'), count: linkedThreatsCount },
    { label: t('detailPage.assets'), count: vulnerability.affectedAssetsCount },
    { label: t('detailPage.controls'), count: vulnerability.compensatingControls.length },
    { label: t('detailPage.suppliers'), count: linkedSuppliersCount },
  ];
  const observedHeaderActions = isPotentialWorkflow
    ? {}
    : {
        onConfirm: () => {
          setConfirmDialogOpen(true);
        },
        onRequestAcceptance: () => {
          setAcceptanceDialogOpen(true);
        },
        onChangeStatus: handleStatusChange,
        onVerify: () => {
          setVerifyDialogOpen(true);
        },
      };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <VulnerabilityDetailHeader
          workflowType={workflowType}
          cveId={displayId}
          title={vulnerability.title}
          severity={vulnerability.severity}
          status={vulnerability.status}
          showSeverity={false}
          onBack={handleBack}
          onEdit={handleEdit}
          onArchive={() => {
            setArchiveDialogOpen(true);
          }}
          onRestore={() => {
            setRestoreDialogOpen(true);
          }}
          {...observedHeaderActions}
        />

        {/* Edit Dialog */}
        <EditVulnerabilityDialog
          vulnerability={vulnerability}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />

        {/* Archive Dialog */}
        <ArchiveVulnerabilityDialog
          vulnerability={vulnerability}
          open={archiveDialogOpen}
          onOpenChange={setArchiveDialogOpen}
          onSuccess={handleArchiveSuccess}
        />

        {/* Restore Dialog */}
        <RestoreVulnerabilityDialog
          vulnerability={vulnerability}
          open={restoreDialogOpen}
          onOpenChange={setRestoreDialogOpen}
          onSuccess={handleRestoreSuccess}
        />

        {/* VUL-STR-023: Confirm Vulnerability Dialog */}
        {!isPotentialWorkflow && (
          <ConfirmVulnerabilityDialog
            vulnerability={vulnerability}
            open={confirmDialogOpen}
            onOpenChange={setConfirmDialogOpen}
            onSuccess={handleConfirmSuccess}
          />
        )}

        {/* VUL-STR-022: Status Change Dialog */}
        {!isPotentialWorkflow && (
          <VulnerabilityStatusChangeDialog
            vulnerability={vulnerability}
            targetStatus={statusChangeTarget}
            open={statusChangeDialogOpen}
            onOpenChange={setStatusChangeDialogOpen}
            onSuccess={handleStatusChangeSuccess}
          />
        )}

        {/* VUL-STR-027: Verify Remediation Dialog */}
        {!isPotentialWorkflow && (
          <VerifyRemediationDialog
            vulnerability={vulnerability}
            open={verifyDialogOpen}
            onOpenChange={setVerifyDialogOpen}
            onSuccess={handleVerifySuccess}
          />
        )}

        {/* VUL-STR-032: Request Acceptance Dialog */}
        {!isPotentialWorkflow && (
          <RequestAcceptanceDialog
            vulnerability={vulnerability}
            open={acceptanceDialogOpen}
            onOpenChange={setAcceptanceDialogOpen}
            onSuccess={handleAcceptanceSuccess}
          />
        )}

        {/* Table-aligned overview */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detailPage.overview')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('detailPage.id')}</p>
                <p className="font-mono text-sm">{displayId}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('detailPage.type')}</p>
                <p className="text-sm font-medium">
                  {idType === 'custom' ? t('detailPage.custom') : t('detailPage.system')}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('detailPage.status')}</p>
                <p className="text-sm font-medium">
                  {workflowType === 'observed' ? t('detailPage.observed') : t('detailPage.potential')}
                </p>
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-xs text-muted-foreground">{t('detailPage.vulnerability')}</p>
                <p className="text-sm font-medium">{vulnerability.title}</p>
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-xs text-muted-foreground">{t('detailPage.linkedFrameworks')}</p>
                {frameworkLabels.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {frameworkLabels.map((framework) => (
                      <Badge key={framework} variant="outline" className="text-[10px]">
                        {framework}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-xs text-muted-foreground">{t('detailPage.tags')}</p>
                {vulnerability.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {vulnerability.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[10px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">-</p>
                )}
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-xs text-muted-foreground">{t('detailPage.linkedItems')}</p>
                <div className="flex flex-wrap gap-1">
                  {linkedItemsSummary.map((item) => (
                    <Badge
                      key={item.label}
                      variant={item.count > 0 ? 'secondary' : 'outline'}
                      className="text-[10px]"
                    >
                      {item.label}: {item.count}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('detailPage.lastUpdate')}</p>
                <p className="text-sm font-medium">
                  {format(new Date(vulnerability.updatedAt), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* General Context & Links */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detailPage.contextAndLinks')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{t('detailPage.description')}</p>
              {vulnerability.description ? (
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {vulnerability.description}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">{t('detailPage.noDescription')}</p>
              )}
            </div>

            <div className="space-y-2 border-t pt-4">
              <p className="text-xs text-muted-foreground">{t('detailPage.connectedItems')}</p>
              <div className="flex flex-wrap gap-1">
                {linkedItemsSummary
                  .filter((item) => item.label === 'Risks' || item.label === 'Threats' || item.label === 'Assets')
                  .map((item) => (
                    <Badge
                      key={item.label}
                      variant={item.count > 0 ? 'secondary' : 'outline'}
                      className="text-[10px]"
                    >
                      {item.label}: {item.count}
                    </Badge>
                  ))}
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">{t('detailPage.risks')}</p>
                {vulnerability.linkedRisks.length > 0 ? (
                  <>
                    {vulnerability.linkedRisks.slice(0, 5).map((risk) => (
                      <div key={risk.id} className="text-sm">
                        {risk.riskTitle}
                      </div>
                    ))}
                    {vulnerability.linkedRisks.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        {t('detailPage.moreLinkedRisks', { count: vulnerability.linkedRisks.length - 5 })}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('detailPage.noLinkedRisks')}</p>
                )}
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">{t('detailPage.threats')}</p>
                {linkedThreatLabels.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {linkedThreatLabels.slice(0, 8).map((threat) => (
                      <Badge key={threat} variant="outline" className="text-[10px]">
                        {threat}
                      </Badge>
                    ))}
                  </div>
                ) : linkedThreatsCount > 0 ? (
                  <p className="text-sm">{t('detailPage.linkedThreatsCount', { count: linkedThreatsCount })}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('detailPage.noLinkedThreats')}</p>
                )}
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">{t('detailPage.assets')}</p>
                {vulnerability.affectedAssets.length > 0 ? (
                  <>
                    {vulnerability.affectedAssets.slice(0, 5).map((asset) => (
                      <div key={asset.id} className="text-sm">
                        {asset.assetName}
                      </div>
                    ))}
                    {vulnerability.affectedAssets.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        {t('detailPage.moreLinkedAssets', { count: vulnerability.affectedAssets.length - 5 })}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('detailPage.noLinkedAssets')}</p>
                )}
              </div>
            </div>

            <div className="border-t pt-4 text-xs text-muted-foreground">
              {vulnerability.recurrenceCount && vulnerability.recurrenceCount > 0 && (
                <div className="mb-2 flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {t('detailPage.recurring', { count: vulnerability.recurrenceCount })}
                  </Badge>
                  {vulnerability.isChronicVulnerability && (
                    <Badge variant="destructive" className="text-xs">
                      {t('detailPage.chronicVulnerability')}
                    </Badge>
                  )}
                </div>
              )}
              {vulnerability.discoveredAt && (
                <p>
                  {t('detailPage.discoveredAgo', { time: formatDistanceToNow(new Date(vulnerability.discoveredAt), { addSuffix: true }) })}
                </p>
              )}
              <p>
                {t('detailPage.createdAgo', { time: formatDistanceToNow(new Date(vulnerability.createdAt), { addSuffix: true }), name: vulnerability.createdBy.displayName })}
              </p>
              <p>
                {t('detailPage.updatedAgo', { time: formatDistanceToNow(new Date(vulnerability.updatedAt), { addSuffix: true }), name: vulnerability.updatedBy.displayName })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Affected Assets */}
        <VulnerabilityAffectedAssets
          vulnerabilityId={vulnerabilityId}
          assets={vulnerability.affectedAssets}
          totalCount={vulnerability.affectedAssetsCount}
          onLinkAssets={handleLinkAssets}
          onOpenLinkDialog={() => { setLinkAssetsDialogOpen(true); }}
          onUpdateStatus={handleUpdateAssetStatus}
          onUnlinkAsset={handleUnlinkAsset}
          isLinking={bulkLinkAssets.isPending}
          isUpdating={updateAssetLink.isPending}
          isUnlinking={unlinkAsset.isPending}
        />

        {/* VUL-STR-020: Bulk Link Assets Dialog */}
        <BulkLinkAssetsDialog
          open={linkAssetsDialogOpen}
          onOpenChange={setLinkAssetsDialogOpen}
          vulnerabilityId={vulnerabilityId}
          vulnerabilityTitle={vulnerability.title}
          onSuccess={() => { void refetch(); }}
        />

        {/* VUL-STR-025, VUL-STR-026: Remediation Status with Progress Tracking */}
        {!isPotentialWorkflow && (
          <VulnerabilityRemediationStatus
            vulnerabilityId={vulnerability.id}
            orgId={activeOrg.id}
            status={vulnerability.status}
            remediationOwner={vulnerability.remediationOwner}
            remediationTasks={vulnerability.remediationTasks}
            confirmedAt={vulnerability.confirmedAt}
            remediatedAt={vulnerability.remediatedAt}
            verifiedAt={vulnerability.verifiedAt}
            closedAt={vulnerability.closedAt}
            canEditOwner
            onCreateTask={() => {
              setCreateTaskDialogOpen(true);
            }}
          />
        )}

        {/* VUL-STR-026: Create Remediation Task Dialog */}
        {!isPotentialWorkflow && (
          <CreateRemediationTaskDialog
            vulnerability={vulnerability}
            orgId={activeOrg.id}
            open={createTaskDialogOpen}
            onOpenChange={setCreateTaskDialogOpen}
            onSuccess={handleCreateTaskSuccess}
          />
        )}

        {/* VUL-STR-034: Compensating Controls */}
        <CompensatingControlsPanel
          vulnerabilityId={vulnerabilityId}
          vulnerabilityStatus={vulnerability.status}
          canManage={vulnerability.status !== 'archived'}
          onAddControl={() => {
            setAddControlDialogOpen(true);
          }}
        />
        <AddCompensatingControlDialog
          vulnerabilityId={vulnerabilityId}
          open={addControlDialogOpen}
          onOpenChange={setAddControlDialogOpen}
        />

        {/* Linked Entities */}
        <VulnerabilityLinkedEntities
          vulnerabilityId={vulnerabilityId}
          linkedRisks={vulnerability.linkedRisks}
          linkedIncidents={vulnerability.linkedIncidents}
          onLinkRisk={() => {
            setLinkRiskDialogOpen(true);
          }}
          onUnlinkRisk={handleUnlinkRisk}
          isUnlinkingRisk={unlinkRisk.isPending}
          canManageRisks
          onCreateRisk={() => {
            setCreateRiskDialogOpen(true);
          }}
        />

        {/* VUL-STR-037: Link Risk Dialog */}
        <LinkRiskDialog
          open={linkRiskDialogOpen}
          onOpenChange={setLinkRiskDialogOpen}
          availableRisks={availableRisksData?.items ?? []}
          isLoadingRisks={isLoadingAvailableRisks}
          isLinking={linkRisk.isPending}
          onSearch={setRiskSearchTerm}
          onLink={handleLinkRisk}
          searchValue={riskSearchTerm}
        />

        {/* VUL-STR-038: Create Risk from Vulnerability Dialog */}
        <CreateRiskFromVulnerabilityDialog
          vulnerability={vulnerability}
          open={createRiskDialogOpen}
          onOpenChange={setCreateRiskDialogOpen}
          onSuccess={handleCreateRiskSuccess}
        />

        {/* Exception Panel */}
        {!isPotentialWorkflow && (
          <VulnerabilityExceptionPanel
            isAccepted={vulnerability.isAccepted}
            acceptanceReason={vulnerability.acceptanceReason}
            acceptanceExpiresAt={vulnerability.acceptanceExpiresAt}
            currentAcceptance={vulnerability.currentAcceptance}
            compensatingControls={vulnerability.compensatingControls}
          />
        )}

        {/* NIS2 Measure Links */}
        <NIS2MeasureLinkSection
          orgId={activeOrg.id}
          sourceEntityId={vulnerabilityId}
          sourceDomain="VUL"
          sourceEntityType="vulnerability"
          sourceEntityTitle={vulnerability.title}
          suggestedMeasures={['e']}
        />

        {/* VUL-STR-061: Change History */}
        <VulnerabilityChangeHistorySection
          vulnerabilityId={vulnerabilityId}
          vulnerabilityName={vulnerability.cveId ?? vulnerability.title}
        />
      </div>
    </div>
  );
}
